import { useEffect, useRef, useState } from "react";
import { CheckSuccessIcon, ChevronDownGreyIcon } from "../../asset/image";
import { useSelectTheme } from "../../store";
import { Button, Checkbox } from "../atoms";
import { RIF, useClickOutside } from "../../engine";

export const PARTICIPANT_STATUS_OPTIONS = ["notJoined", "joined", "completed"] as const;
export type ParticipantStatusFilterOption = (typeof PARTICIPANT_STATUS_OPTIONS)[number];

export const ParticipantStatusFilter = (props: {
  selectedStatusList: ParticipantStatusFilterOption[];
  setSelectedStatusList: (statuses: ParticipantStatusFilterOption[]) => void;
  size?: "large" | "normal";
}) => {
  const { color, fontSize, fontWeight } = useSelectTheme();
  const { selectedStatusList, setSelectedStatusList, size } = props;

  const [tempSelectedStatusList, setTempSelectedStatusList] = useState<
    ParticipantStatusFilterOption[]
  >([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (showDropdown) setTempSelectedStatusList(selectedStatusList);
  }, [showDropdown, selectedStatusList]);

  const ref = useRef<HTMLDivElement | null>(null);
  useClickOutside(ref, () => {
    setShowDropdown(false);
  });

  const handleToggleStatus = (checked: boolean, status: ParticipantStatusFilterOption) => {
    if (checked) {
      setTempSelectedStatusList((prev) => (prev.includes(status) ? prev : [...prev, status]));
    } else {
      setTempSelectedStatusList((prev) => prev.filter((s) => s !== status));
    }
  };

  const handleApply = () => {
    setSelectedStatusList(tempSelectedStatusList);
    setShowDropdown(false);
  };

  const allSelected = selectedStatusList.length === PARTICIPANT_STATUS_OPTIONS.length;
  const buttonText = allSelected
    ? "All Status"
    : `${selectedStatusList.length} Status(es) Selected`;

  return (
    <div
      ref={ref}
      css={{
        position: "relative",
      }}
    >
      <button
        type="button"
        onClick={() => setShowDropdown((prev) => !prev)}
        css={{
          height: size === "large" ? "40px" : "32px",
          width: "200px",
          borderRadius: "5px",
          border: `1px solid ${color.border.default}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 8px",
          background: color.white,
          cursor: "pointer",
        }}
      >
        <p
          css={{
            fontSize: fontSize.h7,
            color: color.textIcon.secondary,
            maxWidth: "140px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {buttonText}
        </p>
        <img src={ChevronDownGreyIcon} width={20} alt="" />
      </button>

      {RIF(
        showDropdown,
        <div
          css={{
            position: "absolute",
            left: "0",
            top: "34px",
            width: "200px",
            borderRadius: "5px",
            border: `1px solid ${color.border.default}`,
            padding: "16px",
            background: color.white,
            zIndex: 100,
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          }}
        >
          {PARTICIPANT_STATUS_OPTIONS.map((status) => {
            const isChecked = tempSelectedStatusList.includes(status);
            const pill =
              status === "joined"
                ? { background: color.tag.green.light, color: color.tag.green.dark }
                : status === "completed"
                  ? { background: color.tag.blue.light, color: color.tag.blue.dark }
                  : { background: color.tag.grey.light, color: color.tag.grey.dark };

            return (
              <label
                key={status}
                css={{
                  display: "flex",
                  alignItems: "center",
                  height: "24px",
                  marginBottom: "4px",
                  cursor: "pointer",
                  gap: "8px",
                }}
              >
                <Checkbox
                  onChange={(e: boolean) => handleToggleStatus(e, status)}
                  value={isChecked}
                />
                <div
                  css={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "4px 8px",
                    borderRadius: "12px",
                    backgroundColor: pill.background,
                    gap: "5px",
                  }}
                >
                  {status === "joined" && (
                    <img src={CheckSuccessIcon} width={14} alt="Joined icon" />
                  )}
                  <span
                    css={{
                      maxWidth: "100%",
                      color: pill.color,
                      fontSize: fontSize.h8,
                      fontWeight: fontWeight.thick,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      pointerEvents: "none",
                    }}
                  >
                    {status === "notJoined"
                      ? "Not joined yet"
                      : status === "joined"
                        ? "Joined"
                        : "Completed"}
                  </span>
                </div>
              </label>
            );
          })}

          <Button
            onClick={handleApply}
            btnPadding={"small"}
            css={{ marginTop: "12px", fontSize: fontSize.h7 }}
            children={"Apply"}
          />
        </div>,
      )}
    </div>
  );
};
